// Dashboard TODAY card, TODAY / WEEK / MONTH views (Joel, 2026-10-18): everything coming up
// from today through the next 7 or 30 days — METIS agenda items, scheduled KYRS
// tasks and KRNOS calendar events — using the same parsing rules as useTodayData.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { parseScheduled } from '../lib/autoSchedule';
import { useDataVersion } from '../live/screenContext';
import { dateKeyOf, eventOccursToday, parseAgendaTimeToday, parseHHMMToday, type RawEvent } from '../olmps/useTodayData';

export type AgendaRange = 'day' | 'week' | 'month';
export const RANGE_DAYS: Record<AgendaRange, number> = { day: 1, week: 7, month: 30 };

export interface RangeEntry {
  id: string;
  title: string;
  day: Date; // local midnight of the day it falls on
  start: Date | null; // null = anytime
}

interface AgendaRow { id: string; date: string; text: string | null; time: string | null; completed: boolean | null }
interface TaskRow { id: string; title: string | null; scheduled: string | null; done: boolean | null; unlisted: boolean | null }

function midnight(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function dayOfKey(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** Upcoming entries over the next `RANGE_DAYS[range]` days (day = today only). */
export function useAgendaRange(userId: string | undefined, range: AgendaRange, events: RawEvent[], now: Date) {
  const [agenda, setAgenda] = useState<AgendaRow[]>([]);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const dataVersion = useDataVersion();
  const days = RANGE_DAYS[range];
  const startKey = dateKeyOf(now);
  const endDate = new Date(midnight(now).getTime());
  endDate.setDate(endDate.getDate() + days - 1);
  const endKey = dateKeyOf(endDate);

  useEffect(() => {
    if (!userId) return;
    let live = true;
    supabase
      .from('agenda_items')
      .select('id, date, text, time, completed')
      .eq('user_id', userId)
      .gte('date', startKey)
      .lte('date', endKey)
      .then((res) => live && !res.error && setAgenda((res.data ?? []) as AgendaRow[]));
    supabase
      .from('tasks')
      .select('id, title, scheduled, done, unlisted')
      .eq('user_id', userId)
      .not('scheduled', 'is', null)
      .then((res) => live && !res.error && setTasks((res.data ?? []) as TaskRow[]));
    return () => {
      live = false;
    };
  }, [userId, range, startKey, endKey, dataVersion]);

  const entries = useMemo(() => {
    const out: RangeEntry[] = [];
    const nowMs = now.getTime();
    for (const a of agenda) {
      if (!a.text || a.completed) continue;
      const day = dayOfKey(a.date);
      if (!day) continue;
      const start = a.time ? parseAgendaTimeToday(a.time, day) : null;
      if (start && start.getTime() < nowMs - 30 * 60000) continue;
      out.push({ id: `agenda-${a.id}`, title: a.text, day, start });
    }
    for (const t of tasks) {
      if (t.done || t.unlisted) continue;
      const sd = parseScheduled(t.scheduled);
      if (!sd) continue;
      const k = dateKeyOf(sd);
      if (k < startKey || k > endKey) continue;
      // A date-only `scheduled` parses to 00:00 — that is "anytime", not midnight.
      const timed = /\d{1,2}:\d{2}/.test(t.scheduled ?? '');
      out.push({ id: `task-${t.id}`, title: t.title || 'Untitled task', day: midnight(sd), start: timed ? sd : null });
    }
    for (let i = 0; i < days; i++) {
      const day = midnight(now);
      day.setDate(day.getDate() + i);
      const key = dateKeyOf(day);
      for (const e of events) {
        if (!e.title || !eventOccursToday(e, key, day)) continue;
        const start = parseHHMMToday(e.start_time, day);
        if (i === 0 && start && start.getTime() < nowMs) continue;
        out.push({ id: `event-${e.id}-${key}`, title: e.title, day, start });
      }
    }
    // Day first; within a day timed items in order, anytime items last.
    out.sort((a, b) => a.day.getTime() - b.day.getTime() || (a.start?.getTime() ?? Infinity) - (b.start?.getTime() ?? Infinity));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, agenda, tasks, events, startKey, endKey]);

  return entries;
}
